import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import { GridService } from 'shared/interfaces/grid-service';
import { NotificationSettings } from 'shared/interfaces/notification-settings.interface';
import { ShijiUserPermission, ShijiUserPermissionRequest } from 'shared/interfaces/shiji-user-permission.interface';
import { CachingService } from 'shared/services/caching.service';
import { User } from '../interfaces/user.interface';

@Injectable({
    providedIn: 'root',
})
export class ShijiUsersService implements GridService {
    private apollo: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
    ) {
        this.apollo = this.apolloProvider.use('usersAPI');
    }

    NAME = 'ShijiUsersGridService';

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query getShijiUsers($orderBy: String,
                    $orderDirection: OrderDirection,
                    $email: String,
                    $firstName: String,
                    $lastName: String,
                    $statuses: [UserStatus],
                    $lastLogin: DateOnly
                ) {
                    shijiUsers {
                        search(searchSettings:
                        {pageNumber:${pageNumber},
                            pageSize: ${pageSize},
                            orderBy: $orderBy,
                            orderDirection: $orderDirection
                        }, filtering: {email: $email,
                            firstName: $firstName,
                            lastName: $lastName,
                            statuses: $statuses,
                            lastLogin: $lastLogin
                        })  {
                            data {
                                id,
                                userType,
                                email,
                                firstName,
                                lastName,
                                emailsEnabled,
                                lastLogin,
                                status,
                                passwordChangeRequired
                            },
                            totalCount
                        }
                    }}`,
                fetchPolicy: 'no-cache',
                variables: {
                    orderBy: params.request.sortModel[0]?.colId,
                    orderDirection: params.request.sortModel[0] ? (params.request.sortModel[0]?.sort.toUpperCase() ?? 'ASC') : undefined,
                    email: (params.request.filterModel as FilterModel)['email']?.filter,
                    firstName: (params.request.filterModel as FilterModel)['firstName']?.filter,
                    lastName: (params.request.filterModel as FilterModel)['lastName']?.filter,
                    lastLogin: (params.request.filterModel as FilterModel)['lastLogin']?.filter
                        ? formatDate((params.request.filterModel as FilterModel)['lastLogin']?.filter, 'yyyy-MM-dd', 'en-US')
                        : undefined,
                    statuses: (params.request.filterModel as FilterModel)['status']?.filter,
                },
            })
            .pipe(
                map((response: any) => {
                    return response.data.shijiUsers.search;
                }),
            );
    }

    public getShijiUserById(userId: string, ignoreCurrentUser: boolean = true): Observable<User> {
        return this.apollo
            .query({
                query: gql`
                query getShijiUserById {
                    shijiUsers {
                        search(filtering:{searchTerm: "${userId}", ignoreCurrentUser: ${ignoreCurrentUser}} ) {
                            data {
                                id
                                firstName
                                lastName
                                email
                                status
                                emailsEnabled
                                lastLogin
                                isEnabled
                                passwordChangeRequired
                                userType,
                                entityTracking {
                                    createdBy,
                                    createdAt,
                                    modifiedBy,
                                    modifiedAt
                                }
                            },
                            totalCount
                        }
                    }}`,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.shijiUsers.search.data[0];
                }),
            );
    }

    public updateCurrentShijiUser(user: User): Observable<any> {
        const shijiUserMutation = `
                mutation updateCurrentShijiUser {
                    shijiUsers {
                        updateCurrentShijiUser(shijiUser: {
                            ${user.firstName ? 'firstName: "' + user.firstName + '"' : ''},
                            ${user.lastName ? 'lastName: "' + user.lastName + '"' : ''}
                        }) {
                            statusCode,
                            message
                        }
                    }
                }`;
        return this.apollo
            .mutate({
                mutation: gql(shijiUserMutation),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.shijiUsers.updateCurrentShijiUser;
                }),
            );
    }

    //Methods for notification settings

    public getNotificationSettingsByUserId(userId: string | string[]): Observable<NotificationSettings[]> {
        let userIds;
        if (!Array.isArray(userId)) {
            userIds = [userId] as string[];
        } else {
            userIds = userId as string[];
        }

        return this.apollo
            .mutate({
                mutation: gql`
                query getNotificationSettings {
                    notificationSettings {
                        search(filtering:{
                            userIds:[${userIds.map((id) => `"${id}"`)}]
                        }){
                            data {
                                id,
                                userId,
                                notificationType,
                                owner,
                                isActive
                            },
                            totalCount,
                            statusCode,
                            message
                        }
                    }
                }`,
            })
            .pipe(
                map((response: any) => {
                    return response.data.notificationSettings.search.data;
                }),
            );
    }

    public updateNotificationSettings(
        notification: NotificationSettings | NotificationSettings[],
        isCurrentUser: boolean = false,
    ): Observable<any> {
        let notifications;
        if (!Array.isArray(notification)) {
            notifications = [notification] as NotificationSettings[];
        } else {
            notifications = notification as NotificationSettings[];
        }

        let array = '';

        for (let i = 0; i < notifications.length; i++) {
            array += `{ id: "${notifications[i].id}"
                        userId: "${notifications[i].userId}",
                        isActive: ${notifications[i].isActive}
                        },`;
        }

        const updateNotifications = `
                mutation ${isCurrentUser ? 'updateCurrentUserNotificationSettings' : 'updateNotificationSettings'} {
                    notificationSettings {
                        ${isCurrentUser ? 'updateCurrentUserNotificationSettings' : 'updateMultipleNotificationSettings'} (
                            notificationSettings: [
                            ${array}
                            ]) {
                            statusCode,
                            message
                        }
                    }
                }`;

        return this.apollo
            .mutate({
                mutation: gql(updateNotifications),
            })
            .pipe(
                map((response: any) => {
                    return isCurrentUser
                        ? response.data.notificationSettings.updateCurrentUserNotificationSettings.data
                        : response.data.notificationSettings.updateMultipleNotificationSettings.data;
                }),
            );
    }

    public updateShijiUser(user: User): Observable<any> {
        const shijiUserMutation = `
                mutation updateShijiUser {
                    shijiUsers {
                        updateShijiUser(
                            shijiUser: {
                                id: "${user.id}",
                                ${user.firstName ? 'firstName: "' + user.firstName + '"' : ''},
                                ${user.lastName ? 'lastName: "' + user.lastName + '"' : ''},
                                ${user.isEnabled !== null && user.isEnabled !== undefined ? 'isEnabled: ' + user.isEnabled : ''},
                                ${user.emailsEnabled !== null && user.emailsEnabled !== undefined ? 'emailsEnabled: ' + user.emailsEnabled : ''},
                                ${user.passwordChangeRequired !== null && user.passwordChangeRequired !== undefined ? 'passwordChangeRequired: ' + user.passwordChangeRequired : ''},
                            }) {
                            statusCode,
                            message
                        }
                    }
                }`;
        return this.apollo
            .mutate({
                mutation: gql(shijiUserMutation),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.shijiUsers.updateShijiUser;
                }),
            );
    }

    public getShijiUserPermissions(userId: string): Observable<ShijiUserPermission[]> {
        return this.apollo
            .query({
                query: gql`
                query getShijiUserPermission {
                    shijiUsers {
                        search(filtering:{searchTerm: "${userId}"} ) {
                            data {
                                shijiPermissions {
                                    id
                                    shijiAccessZone
                                    isAuthorized
                                }
                            }
                        }
                    }}`,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.shijiUsers.search.data[0].shijiPermissions;
                }),
            );
    }

    public updateShijiUserPermission(permission: ShijiUserPermissionRequest): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation updateShijiUser {
                    shijiPermissions {
                        updatePermission(
                            shijiPermission:
                            {
                                id: "${permission.id}",
                                isAuthorized: ${permission.isAuthorized}
                            }
                        ) {
                            data,
                            statusCode,
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.shijiPermissions.updatePermission;
                }),
            );
    }

    public deleteShijiUser(user: User): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation deleteUser {
                    shijiUsers {
                        deleteShijiUser(shijiUserID: "${user.id}") {
                            data,
                            statusCode,
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.shijiUsers.deleteShijiUser.data;
                }),
            );
    }

    //Apollo cache
    public getShijiUserFromCache(userId: string | undefined) {
        return (
            this.apollo.client.cache.readQuery({
                query: gql`
                query readShijiUser($id: ID!) {
                    shijiUsers{
                        search(filtering:{searchTerm: "${userId}"} ) {
                            data {
                                id
                                firstName
                                lastName
                                email
                                userType
                            }
                        }
                    }
                }
            `,
                variables: {
                    id: userId,
                },
            }) as any
        )?.shijiUsers?.search?.data[0];
    }
}
